import { isEmpty } from 'lodash-es';

import { distanceBetweenTwoPoints } from './distance-between-two-points.js';
import { DEFAULT_DATA_POINT_RADIUS } from '../../../../core/constants.js';
import type { Point } from '../../../../core/types/point.js';
import type { AxisIdToScales } from '../../../context/XYChartScales.context.js';
import {
  AnyXYChartScale,
  DotSeriesWithDatapoints,
} from '../../../types/xy-chart-internals.js';
import { DotDatapoint } from '../../../types/xy-chart.js';
import { getCurrentScale } from '../../../utils/get-scale.js';
import { xAccessor, yAccessor } from '../../displays/dot-accessors.js';

export const getDistance = (
  datapoint: DotDatapoint,
  xScale: AnyXYChartScale,
  yScale: AnyXYChartScale,
  relativePosition: Point,
) => {
  const xPixel = xAccessor(xScale)(datapoint);
  const yPixel = yAccessor(yScale)(datapoint);

  return distanceBetweenTwoPoints(
    relativePosition.x,
    xPixel,
    relativePosition.y,
    yPixel,
  );
};

export function getHoveredDotDatapoint(
  relativePosition: Point,
  dotSeries: DotSeriesWithDatapoints[],
  axisIdToScales: AxisIdToScales,
) {
  if (!dotSeries.length) {
    return undefined;
  }

  const accumulator = {
    distance: Infinity,
    datapoint: {},
    series: {},
  } as {
    distance: number;
    datapoint: DotDatapoint;
    series: DotSeriesWithDatapoints;
  };

  let seriesIndex = dotSeries.length - 1;
  while (seriesIndex >= 0) {
    const { data, xAxisId, yAxisId } = dotSeries[seriesIndex];

    const xScale = getCurrentScale(axisIdToScales, xAxisId);
    const yScale = getCurrentScale(axisIdToScales, yAxisId);

    const [minWidth, maxWidth] = xScale.range();

    let datapointIndex = 0;
    while (datapointIndex < data.length) {
      const datapoint = data[datapointIndex];

      const xPixel = xAccessor(xScale)(datapoint);
      const isPointOutOfBounds = xPixel < minWidth || xPixel > maxWidth;

      if (isPointOutOfBounds) {
        datapointIndex += 1;
        continue;
      }

      const distance = getDistance(datapoint, xScale, yScale, relativePosition);

      if (distance < accumulator.distance) {
        accumulator.distance = distance;
        accumulator.datapoint = datapoint;
        accumulator.series = dotSeries[seriesIndex];
      }

      if (distance <= DEFAULT_DATA_POINT_RADIUS) {
        break;
      }

      datapointIndex += 1;
    }

    if (accumulator.distance <= DEFAULT_DATA_POINT_RADIUS) {
      break;
    }

    seriesIndex -= 1;
  }

  if (isEmpty(accumulator.datapoint) || isEmpty(accumulator.series)) {
    return undefined;
  }

  return { datapoint: accumulator.datapoint, series: accumulator.series };
}
